/**
 * Head-wise discount per student (setting: fee_head_wise_discount).
 *  - installmentId given -> only that installment (✎ from the fee table)
 *  - otherwise           -> all installments of the admission (grid)
 * Rules (checked again on the server):
 *  completed = locked · partial = new due must stay >= already paid · pending = free
 */
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Table, Form, Badge, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import schoolApi from "../../api/schoolApi";

const num = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const inr = (v) => Math.round(num(v)).toLocaleString("en-IN");

export const calcHeadDiscount = (amount, type, value) => {
  const amt = Math.max(0, num(amount));
  const v = num(value);
  if (!type || v <= 0) return 0;
  if (type === "PERCENT") return Math.round((amt * Math.min(100, v)) / 100);
  if (type === "FLAT") return Math.min(Math.round(v), amt);
  return 0;
};

const toEditable = (inst) => ({
  ...inst,
  heads: (inst.heads || []).map((h) => ({
    ...h,
    discount_type: h.discount_type || "FLAT",
    discount_value: num(h.discount_value) || "",
    had_discount: !!h.discount_type,
    discount_remark: h.discount_remark || "",
  })),
});

const HeadDiscountModal = ({ show, onHide, installmentId, admissionId, sessionId, studentName, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [bulk, setBulk] = useState({ head: "", type: "FLAT", value: "" });
  const single = !!installmentId;

  useEffect(() => {
    if (!show) return;
    (async () => {
      setLoading(true);
      try {
        const res = single
          ? await schoolApi.getInstallmentHeadDiscounts(installmentId)
          : await schoolApi.getStudentHeadDiscounts(admissionId, sessionId);
        const list = single ? (res?.result ? [res.result] : []) : res?.result || [];
        setRows(list.map(toEditable));
      } catch (e) {
        toast.error(e?.response?.data?.message || "Could not load fee heads");
      } finally {
        setLoading(false);
      }
    })();
  }, [show, installmentId, admissionId, sessionId]);

  const headNames = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => r.heads.forEach((h) => m.set(String(h.fee_head_master_id), h.headname)));
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [rows]);

  const summary = (inst) => {
    const oldHead = (inst.heads || []).reduce((a, h) => a + num(h.discount_amount), 0);
    const newHead = inst.heads.reduce((a, h) => a + calcHeadDiscount(h.amount, h.discount_type, h.discount_value), 0);
    const other = Math.max(0, num(inst.installment_discount) - oldHead);
    const total = other + newHead;
    const gross = num(inst.amount) + num(inst.transport_fee) + num(inst.previous_due);
    const due = Math.max(0, gross - total);
    const paid = num(inst.deposit_amount);
    const maxDisc = Math.max(0, gross - paid);
    return { other, newHead, total, due, paid, maxDisc, error: !inst.locked && total > maxDisc };
  };

  const setHead = (instId, headId, field, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id !== instId
          ? r
          : { ...r, heads: r.heads.map((h) => (String(h.fee_head_master_id) === String(headId) ? { ...h, [field]: value } : h)) }
      )
    );
  };

  const applyBulk = () => {
    if (!bulk.head) return toast.warn("Select a fee head");
    if (num(bulk.value) < 0) return toast.warn("Discount can not be negative");
    if (bulk.type === "PERCENT" && num(bulk.value) > 100) return toast.warn("Percent can not be more than 100");
    setRows((prev) =>
      prev.map((r) =>
        r.locked
          ? r
          : {
              ...r,
              heads: r.heads.map((h) =>
                String(h.fee_head_master_id) === bulk.head
                  ? { ...h, discount_type: bulk.type, discount_value: bulk.value }
                  : h
              ),
            }
      )
    );
  };

  const buildPayload = (inst) =>
    inst.heads.map((h) => ({
      fee_head_master_id: h.fee_head_master_id,
      discount_type: num(h.discount_value) > 0 ? h.discount_type : null,
      discount_value: num(h.discount_value),
      discount_remark: h.discount_remark || null,
    }));

  const save = async () => {
    const open = rows.filter((r) => !r.locked);
    const bad = open.find((r) => summary(r).error);
    if (bad) return toast.error(`${bad.month}: discount is more than the unpaid amount`);
    for (const r of open)
      for (const h of r.heads) {
        if (num(h.discount_value) < 0) return toast.error("Discount can not be negative");
        if (h.discount_type === "PERCENT" && num(h.discount_value) > 100) return toast.error("Percent can not be more than 100");
        if (h.discount_type === "FLAT" && num(h.discount_value) > num(h.amount))
          return toast.error(`${r.month} – ${h.headname}: discount is more than the head amount`);
      }
    setSaving(true);
    try {
      const res = single
        ? await schoolApi.saveInstallmentHeadDiscounts(installmentId, buildPayload(open[0] || rows[0]))
        : await schoolApi.saveStudentHeadDiscounts(
            admissionId,
            open.map((r) => ({ id: r.id, heads: buildPayload(r) })),
            sessionId
          );
      if (res?.success) {
        toast.success("Discount saved");
        onSaved && onSaved();
        onHide();
      } else toast.error(res?.message || "Could not save");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const renderInstallment = (inst) => {
    const s = summary(inst);
    return (
      <div key={inst.id} className="mb-3 border rounded">
        <div className="d-flex flex-wrap justify-content-between align-items-center px-3 py-2" style={{ background: "#f1f3f5" }}>
          <div>
            <strong>{inst.month}</strong>{" "}
            {inst.locked ? (
              <Badge bg="secondary">Paid · locked</Badge>
            ) : inst.status === "partial" ? (
              <Badge bg="warning" text="dark">Partly paid ₹{inst.paid ? inr(inst.paid) : 0}</Badge>
            ) : (
              <Badge bg="success">Pending</Badge>
            )}
          </div>
          <small className="text-muted">
            Other discount ₹{inr(s.other)} · Head discount ₹{inr(s.newHead)} ·{" "}
            <strong className={s.error ? "text-danger" : ""}>Due ₹{inr(s.due)}</strong>
            {s.paid > 0 && !inst.locked && <> · max discount ₹{inr(s.maxDisc)}</>}
          </small>
        </div>
        <Table size="sm" className="mb-0 align-middle" responsive>
          <thead>
            <tr>
              <th>Fee Head</th>
              <th className="text-end">Amount</th>
              <th style={{ width: 230 }}>Discount</th>
              <th className="text-end">Discount ₹</th>
              <th className="text-end">Net</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            {inst.heads.length === 0 && (
              <tr>
                <td colSpan={6} className="text-muted text-center">No fee heads found for this installment</td>
              </tr>
            )}
            {inst.heads.map((h) => {
              const d = calcHeadDiscount(h.amount, h.discount_type, h.discount_value);
              return (
                <tr key={h.fee_head_master_id}>
                  <td>{h.headname}</td>
                  <td className="text-end">{inr(h.amount)}</td>
                  <td>
                    <InputGroup size="sm">
                      <Form.Select
                        size="sm"
                        style={{ maxWidth: 80 }}
                        value={h.discount_type}
                        disabled={inst.locked}
                        onChange={(e) => setHead(inst.id, h.fee_head_master_id, "discount_type", e.target.value)}
                      >
                        <option value="FLAT">₹</option>
                        <option value="PERCENT">%</option>
                      </Form.Select>
                      <Form.Control
                        type="number"
                        min="0"
                        max={h.discount_type === "PERCENT" ? 100 : num(h.amount)}
                        placeholder="0"
                        value={h.discount_value}
                        disabled={inst.locked}
                        onKeyDown={(e) => ["-", "e", "E", "+"].includes(e.key) && e.preventDefault()}
                        onChange={(e) => setHead(inst.id, h.fee_head_master_id, "discount_value", e.target.value)}
                      />
                    </InputGroup>
                  </td>
                  <td className={"text-end " + (d > 0 ? "text-success fw-semibold" : "")}>{d > 0 ? `- ${inr(d)}` : "0"}</td>
                  <td className="text-end">{inr(num(h.amount) - d)}</td>
                  <td>
                    <Form.Control
                      size="sm"
                      maxLength={255}
                      value={h.discount_remark}
                      disabled={inst.locked}
                      placeholder="Reason"
                      onChange={(e) => setHead(inst.id, h.fee_head_master_id, "discount_remark", e.target.value)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  const anyOpen = rows.some((r) => !r.locked);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>
          Head-wise Discount{studentName ? ` – ${studentName}` : ""}
          {single && rows[0] ? ` – ${rows[0].month}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : (
          <>
            {!single && anyOpen && headNames.length > 0 && (
              <Row className="g-2 align-items-end mb-3 p-2 border rounded" style={{ background: "#fbfbfd" }}>
                <Col md={4}>
                  <Form.Label className="mb-1 small">Apply to all unpaid installments</Form.Label>
                  <Form.Select size="sm" value={bulk.head} onChange={(e) => setBulk({ ...bulk, head: e.target.value })}>
                    <option value="">Select fee head</option>
                    {headNames.map((h) => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={bulk.type} onChange={(e) => setBulk({ ...bulk, type: e.target.value })}>
                    <option value="FLAT">₹ Flat</option>
                    <option value="PERCENT">% Percent</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    placeholder="Value"
                    value={bulk.value}
                    onKeyDown={(e) => ["-", "e", "E", "+"].includes(e.key) && e.preventDefault()}
                    onChange={(e) => setBulk({ ...bulk, value: e.target.value })}
                  />
                </Col>
                <Col md={2}>
                  <Button size="sm" variant="outline-primary" onClick={applyBulk}>Apply</Button>
                </Col>
              </Row>
            )}
            {rows.length === 0 && <div className="text-muted text-center py-4">No installments found</div>}
            {rows.map(renderInstallment)}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>Close</Button>
        {anyOpen && (
          <Button variant="primary" onClick={save} disabled={saving || loading}>
            {saving ? "Saving..." : "Save Discount"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default HeadDiscountModal;
